import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Crown, Dices, Fingerprint, Loader2, RotateCcw, Trophy, XCircle } from "lucide-react";
import { Die } from "@/components/Dice";
import { api, errCode, formatXAF } from "@/lib/api";
import { useRealtimeEvent } from "@/lib/realtime";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { playDice, playWin } from "@/lib/sound";
import { SoundToggle } from "@/components/SoundToggle";
import { GameChat } from "@/components/GameChat";
import { BackToLobby } from "@/components/BackToLobby";

const REVEAL_MS = 2800;

const useClock = (game) => {
  const [now, setNow] = useState(Date.now());
  const offset = useRef(0);
  useEffect(() => {
    if (game?.server_now) offset.current = Date.parse(game.server_now) - Date.now();
  }, [game?.server_now]);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);
  const left = (iso) => (iso ? Math.max(0, Math.ceil((Date.parse(iso) - (now + offset.current)) / 1000)) : 0);
  return left;
};

const Stat = ({ label, value, cls = "", testId }) => (
  <div className="text-center">
    <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`font-mono text-sm font-semibold ${cls}`} data-testid={testId}>{value}</p>
  </div>
);

const Header = ({ g }) => {
  const { t, lang } = useI18n();
  return (
    <div className="surface grid grid-cols-3 gap-2 p-4" data-testid="game-header">
      <Stat label={t("lobby.pot")} value={formatXAF(g.pot, lang)} testId="game-pot" />
      <Stat label={`${t("lobby.commission")} ${g.commission_percent}%`} value={formatXAF(g.commission, lang)} cls="text-red-300" testId="game-commission" />
      <Stat label={t("lobby.gain")} value={formatXAF(g.potential_gain, lang)} cls="text-emerald-400" testId="game-potential-gain" />
    </div>
  );
};

const PlayerRow = ({ g, p, me, idx }) => {
  const { t } = useI18n();
  const name = g.players.find((x) => x.user_id === p)?.pseudo || p;
  const dice = g.current_rolls[p];
  const prev = g.rounds.length ? g.rounds[g.rounds.length - 1].results.find((r) => r.player_id === p) : null;
  const shown = dice || (g.status !== "in_progress" && g.status !== "starting" ? prev?.dice : null);
  const out = g.status === "in_progress" && !g.contenders.includes(p);
  const turn = g.current_player === p;
  const winner = g.winners?.includes(p) && ["finished", "settling"].includes(g.status);
  const lastMine = [...(g.roll_log || [])].reverse().find((r) => r.player_id === p);
  return (
    <li className={`flex items-center gap-3 rounded-xl border px-3 py-3 transition-colors duration-300 ${turn ? "border-amber-400/60 bg-amber-500/10" : "border-white/5"} ${out ? "opacity-40" : ""}`} data-testid={`game-player-${idx}`}>
      <span className="font-mono text-xs text-slate-600">{idx + 1}</span>
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full font-display font-bold ${p === me ? "bg-amber-500 text-[#0B0D12]" : "bg-white/10"}`}>
        {name.slice(0, 2).toUpperCase()}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-semibold">
        {name} {p === me && <span className="text-xs text-amber-400">({t("game.you")})</span>}
        {winner && <Crown className="ml-1 inline h-4 w-4 text-amber-400" />}
        {shown && lastMine?.auto && <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase text-slate-400" data-testid={`game-player-auto-${idx}`}>{t("game.auto")}</span>}
      </span>
      {shown ? (
        <span className="flex items-center gap-2">
          {shown.map((d, i) => <Die key={i} value={d} size={26} flat ivory={i === 1} />)}
          <span className="w-6 text-right font-mono font-semibold" data-testid={`game-player-total-${idx}`}>{shown.reduce((a, b) => a + b, 0)}</span>
        </span>
      ) : turn ? <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> : null}
    </li>
  );
};

const RollStage = ({ g, me, left, revealing }) => {
  const { t, tErr } = useI18n();
  const [busy, setBusy] = useState(false);
  const [spins, setSpins] = useState(0);
  const last = g.last_roll;
  useEffect(() => {
    if (last) {
      setSpins((s) => s + 3);
      playDice();
    }
  }, [last?.at]); // eslint-disable-line react-hooks/exhaustive-deps
  const name = (id) => g.players.find((x) => x.user_id === id)?.pseudo || id;
  const mine = g.current_player === me;
  const roll = async () => {
    setBusy(true);
    try {
      await api.post(`/games/${g.id}/roll`);
    } catch (e) {
      toast.error(tErr(errCode(e)));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="surface relative overflow-hidden p-6 text-center" data-testid="game-roll-stage">
      <div className="absolute left-1/2 top-0 h-40 w-40 -translate-x-1/2 rounded-full bg-amber-500/15 blur-3xl" />
      <div className="relative flex min-h-[110px] items-center justify-center gap-6">
        <Die value={last?.dice?.[0] || 6} size={84} spins={spins} testId="game-big-die-0" />
        <Die value={last?.dice?.[1] || 5} size={70} spins={spins} ivory testId="game-big-die-1" />
      </div>
      {last && (
        <p className="relative mt-4 text-sm text-slate-400" data-testid="game-last-roll">
          {name(last.player_id)} : <span className="font-mono font-semibold text-white">{last.dice.reduce((a, b) => a + b, 0)}</span>
          {last.auto && <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase">{t("game.auto")}</span>}
        </p>
      )}
      {g.round_no > 0 && <p className="relative mt-2 text-xs font-semibold uppercase tracking-wider text-amber-400" data-testid="game-reroll-banner">{t("game.reroll", { n: g.round_no })}</p>}
      <div className="relative mt-5">
        {revealing ? (
          <p className="text-sm font-semibold text-amber-300" data-testid="game-final-reveal">{t("game.finalReveal")}</p>
        ) : mine ? (
          <button onClick={roll} disabled={busy} data-testid="game-roll-btn"
            className="pulse-ring mx-auto flex h-16 w-full max-w-xs items-center justify-center gap-3 rounded-full bg-amber-500 font-display text-lg font-extrabold text-[#0B0D12] transition-transform active:scale-95 disabled:opacity-60">
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Dices className="h-6 w-6" />} {t("game.roll")} · <span className="font-mono">{left}s</span>
          </button>
        ) : (
          <p className="text-sm text-slate-300" data-testid="game-turn-of">{t("game.turnOf", { name: name(g.current_player) })} · <span className="font-mono text-amber-300">{left}s</span></p>
        )}
      </div>
    </section>
  );
};

const EndPanel = ({ g, me }) => {
  const { t, lang } = useI18n();
  const name = (id) => g.players.find((x) => x.user_id === id)?.pseudo || id;
  if (g.status === "cancelled") {
    return (
      <section className="surface p-6 text-center" data-testid="game-cancelled">
        <XCircle className="mx-auto h-12 w-12 text-red-400" />
        <h2 className="mt-3 font-display text-xl font-bold">{t("game.cancelled")}</h2>
        <p className="mt-2 text-sm text-slate-400">{t("game.cancelledText")}</p>
        <Link to="/lobby" className="mt-5 inline-flex h-12 items-center rounded-full bg-amber-500 px-6 font-semibold text-[#0B0D12]" data-testid="game-back-lobby-btn">{t("game.again")}</Link>
      </section>
    );
  }
  if (g.status === "settling") return <section className="surface p-6 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /> {t("game.settling")}</section>;
  const payout = g.settlement?.payouts?.[me] || 0;
  const won = payout > 0;
  const split = g.winners.length > 1;
  return (
    <section className="surface p-6 text-center" data-testid="game-end-panel">
      {won ? <Trophy className="mx-auto h-14 w-14 text-amber-400" /> : <Dices className="mx-auto h-12 w-12 text-slate-500" />}
      <h2 className="mt-3 font-display text-2xl font-extrabold" data-testid="game-end-title">{won ? t(split ? "game.splitTitle" : "game.winnerTitle") : t("game.lostTitle")}</h2>
      <p className="mt-2 text-sm text-slate-300" data-testid="game-end-text">
        {won ? t("game.youWon", { amount: formatXAF(payout, lang) }) : t("game.winnerIs", { name: g.winners.map(name).join(", "), amount: formatXAF(g.settlement?.share || 0, lang) })}
      </p>
      {!won && <p className="mt-1 text-xs text-slate-500">{t("game.lostText", { amount: formatXAF(g.stake, lang) })}</p>}
      <div className="mt-4 break-all rounded-xl bg-black/30 p-3 text-left font-mono text-[10px] text-slate-500">
        <p>{t("game.hash")} : {g.seed_hash}</p>
        <p className="mt-1 text-slate-400" data-testid="game-revealed-seed">{t("game.seed")} : {g.seed}</p>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Link to={`/verify?game=${g.id}`} className="flex h-12 items-center justify-center gap-2 rounded-full border border-amber-500/40 text-sm font-semibold text-amber-300" data-testid="game-verify-link">
          <Fingerprint className="h-4 w-4" /> {t("game.verify")}
        </Link>
        <Link to="/lobby" className="flex h-12 items-center justify-center gap-2 rounded-full bg-amber-500 font-semibold text-[#0B0D12]" data-testid="game-play-again-btn">
          <RotateCcw className="h-4 w-4" /> {t("game.again")}
        </Link>
      </div>
    </section>
  );
};

export default function GamePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, tErr } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [g, setG] = useState(null);
  const left = useClock(g);

  useEffect(() => {
    api.get(`/games/${id}`).then((r) => setG(r.data)).catch((e) => {
      toast.error(tErr(errCode(e)));
      navigate("/lobby");
    });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fallback if the live connection silently dropped (mobile in background).
  useEffect(() => {
    const id_ = setInterval(() => {
      if (document.visibilityState !== "visible") { return; }
      api.get(`/games/${id}`).then((r) => setG((old) => (!old || ["finished", "cancelled"].includes(old.status) ? old || r.data
        : (r.data.version ?? 0) >= (old.version ?? 0) ? r.data : old))).catch(() => {});
    }, 3000);
    return () => clearInterval(id_);
  }, [id]);

  useRealtimeEvent((m) => {
    if (m.type === "game_state" && m.game.id === id) {
      setG((old) => {
        if (old?.status !== "finished" && m.game.status === "finished" && m.game.winners?.includes(user?.id)) setTimeout(playWin, REVEAL_MS);
        return m.game;
      });
      if (["finished", "cancelled"].includes(m.game.status)) qc.invalidateQueries({ queryKey: ["wallet"] });
    }
  });

  const status = g?.status;
  const prevStatus = useRef(null);
  const [revealing, setRevealing] = useState(false);
  useEffect(() => {
    // Show the deciding roll before the result panel (the last roll ends the game instantly).
    if (prevStatus.current === "in_progress" && ["finished", "settling"].includes(status)) {
      setRevealing(true);
      setTimeout(() => setRevealing(false), REVEAL_MS);
    }
    prevStatus.current = status;
  }, [status]);

  if (!g) return <div className="py-24 text-center text-slate-500">{t("common.loading")}</div>;
  const done = ["finished", "cancelled", "settling"].includes(g.status) && !revealing;
  return (
    <div className="mx-auto max-w-xl space-y-4 rise" data-testid="game-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {done && <BackToLobby to="/lobby" testId="game-header-back" />}
          <h1 className="font-display text-xl font-extrabold">{t("game.title", { n: g.player_count })}</h1>
        </div>
        <span className="sr-only" data-testid="game-status">{g.status}</span>
        <div className="flex items-center gap-2"><GameChat kind="dice" gid={id} /><SoundToggle /></div>
      </div>
      <Header g={g} />
      {g.status === "starting" && (
        <section className="surface p-8 text-center" data-testid="game-countdown">
          {g.waiting_for?.length ? (
            <p className="text-base font-semibold text-amber-300" data-testid="game-waiting-for">{t("game.waitingFor", { names: g.waiting_for.join(", ") })}</p>
          ) : (
            <>
              <p className="text-sm text-slate-400">{t("game.startsIn")}</p>
              <p className="mt-2 font-display text-6xl font-extrabold text-amber-400">{left(g.starts_at)}</p>
            </>
          )}
        </section>
      )}
      {(g.status === "in_progress" || revealing) && <RollStage g={g} me={user.id} left={left(g.turn_deadline)} revealing={revealing} />}
      {done && <EndPanel g={g} me={user.id} />}
      <section className="surface p-4" data-testid="game-players">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{t("game.order")}</p>
        <ul className="space-y-2">{g.order.map((p, i) => <PlayerRow key={p} g={g} p={p} me={user.id} idx={i} />)}</ul>
      </section>
      {g.rounds.length > 0 && (
        <section className="surface p-4" data-testid="game-rounds">
          {g.rounds.map((r) => (
            <div key={r.round} className="border-b border-white/5 py-2 text-xs text-slate-400 last:border-0">
              <span className="font-semibold text-slate-300">{r.round === 0 ? t("game.round0") : t("game.reroll", { n: r.round })}</span> ·{" "}
              {r.results.map((x) => `${g.players.find((p) => p.user_id === x.player_id)?.pseudo}: ${x.total}`).join(" · ")}
            </div>
          ))}
        </section>
      )}
      <p className="break-all text-center font-mono text-[10px] text-slate-600" data-testid="game-seed-hash">sha256 {g.seed_hash}</p>
    </div>
  );
}
